import "../../css/Subscription.css";
import "../../css/GlobalStyles.css";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { API } from "../../API";
import {
  Stripe,
  StripeElements,
  StripeAddressElementChangeEvent,
} from "@stripe/stripe-js";
import { Elements, AddressElement, useElements } from "@stripe/react-stripe-js";
import mixpanel from "mixpanel-browser";
import SubscriptionCheckoutForm, {
  SubscriptionCheckoutType,
} from "../../components/Subscription/SubscriptionCheckoutForm";
import AgreeToTerms from "../../components/AgreeToTerms";
import CheckoutTotalSummary from "../CheckoutTotalSummary";
import { useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import { Colors } from "../../utils/colors";
import {
  checkValidUserReferralCode,
  clearSavedReferralCode,
  createSubscriptionReferral,
  getSavedReferralCode,
} from "../../utils/subscriptionUtils";
import { useOnMount } from "../../hooks/useOnMount";
import {
  AccountData,
  PlanTypeV4,
  SubscriptionPlanType,
  subscriptionPricesV4,
  testSubscriptionPricesV4,
} from "@markit/common.types";
import RectangleButton from "../../components/Buttons/RectangleButton";
import AlertContainer from "../../components/Containers/AlertContainer";
import { SubscriptionStage } from "./SubscriptionPlansFlow";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import { Icon } from "@iconify/react";
import CustomTextField from "../CustomTextField";
import { CircularProgress } from "@mui/material";
import { isDesktop } from "react-device-detect";
import { convertToMoney, isValidEmail } from "@markit/common.utils";
import { getStripePromise, testStripe } from "../../utils/stripeAccountUtils";

type SubscriptionCheckoutProps = {
  subscriptionPlan: SubscriptionPlanType;
  setSubscriptionStage: (subscriptionStage: SubscriptionStage) => void;
  email: string;
  setEmail: (email: string) => void;
  taxAmount: number;
  setTaxAmount: (taxAmount: number) => void;
};

// Renders the billing AddressElement and lifts its Elements instance up so the
// parent can call elements.submit() to trigger Stripe's native field-level
// validation (red outlines + inline messages). Must live inside <Elements>.
const SubscriptionBillingAddress = (props: {
  onChange: (event: StripeAddressElementChangeEvent) => void;
  onElementsReady: (elements: StripeElements | null) => void;
  defaultName?: string;
}) => {
  const { onChange, onElementsReady, defaultName } = props;
  const elements = useElements();
  useEffect(() => {
    onElementsReady(elements);
  }, [elements, onElementsReady]);
  return (
    <AddressElement
      // Pre-fill the name from the account so the user doesn't retype it.
      options={{ mode: "billing", defaultValues: { name: defaultName } }}
      onChange={onChange}
    />
  );
};

const SubscriptionCheckout = memo(function SubscriptionCheckoutFn(
  props: SubscriptionCheckoutProps
) {
  const {
    subscriptionPlan,
    setSubscriptionStage,
    email,
    setEmail,
    taxAmount,
    setTaxAmount,
  } = props;
  const { account } = useSelector(getAccountState);
  const { accountData } = account;
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [codeInput, setCodeInput] = useState<string>("");
  const [applyCodeError, setApplyCodeError] = useState("");
  const [trialSubscription, setTrialSubscription] = useState(false);
  const [referrerUser, setReferrerUser] = useState<AccountData | undefined>();
  const [showingPaymentSheet, setShowingPaymentSheet] =
    useState<boolean>(false);
  const [options, setOptions] = useState<{
    clientSecret: string;
    appearance: {};
  }>();
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreeToTermsError, setAgreeToTermsError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [alertText, setAlertText] = useState({ heading: "", subHeading: "" });
  // Billing address is required for Stripe Tax to calculate sales tax. It must
  // be collected before the subscription is created on the backend.
  const [billingAddress, setBillingAddress] =
    useState<StripeAddressElementChangeEvent["value"]["address"]>();
  const [addressComplete, setAddressComplete] = useState(false);
  const [addressElements, setAddressElements] = useState<StripeElements | null>(
    null
  );

  const styles = {
    headerText: { color: Colors.BLACK, fontSize: 14, fontWeight: "500" },
    buttonText: { fontWeight: "500", fontSize: 14 },
    errorText: { fontSize: 12, color: Colors.RED3 },
  };

  const CODEWORDS = useMemo(() => ["FREETRIAL30", "TECHSTARS"], []); // Trial Codes

  // Final price with trial free price applied or original selected price (in cents)
  const finalPrice = useMemo(
    () => (trialSubscription ? 0 : subscriptionPlan.price),
    [trialSubscription, subscriptionPlan.price]
  );

  const subscriptionOptionIndex = useMemo(
    () =>
      subscriptionPlan === PlanTypeV4.ESSENTIAL
        ? 0
        : subscriptionPlan === PlanTypeV4.PRO
          ? 1
          : 2,
    [subscriptionPlan]
  );

  // Valid promo code if it's a trial or a referral
  const validPromoCode = useMemo(
    () => trialSubscription || referrerUser !== undefined,
    [trialSubscription, referrerUser]
  );

  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    if (agreedToTerms) {
      mixpanel.track("Webapp: Agreed to Terms", {
        distinct_id: accountData.uid,
        type: "Subscription",
        subsription_plan: subscriptionPlan.name,
      });
    }
  }, [
    accountData.uid,
    agreedToTerms,
    subscriptionPlan.name,
    trialSubscription,
  ]);

  // referralCode: the referrer's username, passed in rather than read from
  // referrerUser so a re-fetch right after applying a code doesn't use stale state
  const fetchPaymentSheetParams = useCallback(
    async (isTrial: boolean, referralCode: string | undefined) => {
      setOptions(undefined);

      (testStripe(accountData.uid)
        ? API.user.initializeTestSubscription
        : API.user.initializeSubscription)({
        uid: accountData.uid,
        email: email,
        prices: testStripe(accountData.uid)
          ? [
              testSubscriptionPricesV4[subscriptionOptionIndex].id,
              testSubscriptionPricesV4[subscriptionOptionIndex].overflow,
            ]
          : [
              subscriptionPricesV4[subscriptionOptionIndex].id,
              subscriptionPricesV4[subscriptionOptionIndex].overflow,
            ],
        isTrial: isTrial,
        // Stripe's AddressElement types `line2` as `string | null`, but the
        // subscription request type (@markit/common.api) types it as
        // `string | undefined` — which mirrors Stripe's customers.update input
        // param (line2 is an optional string there, not nullable). These are
        // two different Stripe types: the Element's *output* vs the API's
        // *input*. We reconcile them here, at the boundary, by mapping
        // null -> undefined rather than widening the shared contract — doing
        // the latter would leak a webapp-only UI quirk into a type the backend
        // (and mobile) also consume, and would forward `null` down to Stripe.
        // TODO: if another surface ever collects a Stripe address, extract a
        // shared, typed "AddressElement value -> API address" mapper (e.g. in
        // common.utils) so this null -> undefined conversion lives in one place
        // instead of inline here.
        address: billingAddress
          ? { ...billingAddress, line2: billingAddress.line2 ?? undefined }
          : undefined,
        referralCode,
      })
        .then(async (res: any) => {
          const { clientSecret, tax } = res;
          setTaxAmount(tax ?? 0);
          // get stripe info
          const stripeOptions = {
            // passing the client secret obtained in step 2
            clientSecret: clientSecret,
            // Fully customizable with appearance API.
            appearance: {
              theme: "stripe",
              variables: {
                colorBackground: Colors.WHITE,
              },
            },
          };
          setOptions(stripeOptions);
        })
        .catch((err: any) => {
          console.error("Error on initialize: " + err.message);
        });
    },
    [
      accountData.uid,
      email,
      subscriptionOptionIndex,
      billingAddress,
      setTaxAmount,
    ]
  );

  // prefilled: the code came from a shared referral link, not typed in
  const verifyCode = useCallback(
    async (code: string, prefilled: boolean) => {
      if (code.length === 0) {
        setApplyCodeError("You must enter a code to apply.");
        return;
      }
      const cleanedCodeInput = code.trim();

      // Check if code matches a valid username (referral code)
      const { codeError, userData } =
        await checkValidUserReferralCode(cleanedCodeInput);
      const isValidCode =
        CODEWORDS.includes(cleanedCodeInput) ||
        (userData !== undefined && !codeError);

      mixpanel.track("Webapp: Entered Code For Trial Subscription", {
        subscription_plan: subscriptionPlan.name,
        subscription_plan_amount: subscriptionPlan.price,
        code_input: cleanedCodeInput,
        successful_code: isValidCode,
        prefilled,
      });

      if (isValidCode) {
        if (userData) {
          setReferrerUser(userData);
        } else {
          setTrialSubscription(true);
        }
        setApplyCodeError("");
        // Re-fetch payment sheet if it's currently visible. Only a trial code
        // makes it a trial; a referral code is a normal paid subscription.
        if (showingPaymentSheet) {
          fetchPaymentSheetParams(!userData, userData?.username);
        }
      } else {
        setApplyCodeError(codeError || "Invalid code");
      }
    },
    [CODEWORDS, fetchPaymentSheetParams, showingPaymentSheet, subscriptionPlan]
  );

  const verifyCodeOnPress = useCallback(
    () => verifyCode(codeInput, false),
    [codeInput, verifyCode]
  );

  // Pre-fill and apply the code from a referral link (?referral=CODE), unless
  // it's the user's own code
  useOnMount(() => {
    const savedCode = getSavedReferralCode();
    if (
      savedCode === "" ||
      savedCode.toLowerCase() === accountData.username.toLowerCase()
    ) {
      return;
    }
    setShowCodeInput(true);
    setCodeInput(savedCode);
    verifyCode(savedCode, true);
  });

  const confirmSubscriptionReferral = useCallback(async () => {
    if (referrerUser) {
      await createSubscriptionReferral(referrerUser, accountData.uid);
    }
    clearSavedReferralCode();
  }, [referrerUser, accountData.uid]);

  const subscribe = useCallback(async () => {
    if (purchasing) {
      return;
    }
    let showError = false;
    if (!agreedToTerms) {
      setAgreeToTermsError(true);
      showError = true;
    }
    if (email === "" || !isValidEmail(email)) {
      setEmailError(true);
      showError = true;
    }
    if (!addressComplete || !billingAddress) {
      // Trigger the AddressElement's native field-level validation so the user
      // sees red outlines + inline messages on the specific fields that need
      // attention, instead of one generic error at the bottom.
      if (addressElements) {
        await addressElements.submit();
      }
      showError = true;
    }
    if (showError) {
      return;
    }
    setShowingPaymentSheet(true);
    fetchPaymentSheetParams(trialSubscription, referrerUser?.username);
  }, [
    addressComplete,
    addressElements,
    agreedToTerms,
    billingAddress,
    email,
    fetchPaymentSheetParams,
    purchasing,
    referrerUser,
    trialSubscription,
  ]);

  const onConfirmApplePay = useCallback(async () => {
    if (mixpanel) {
      mixpanel.track("Webapp: Getting Subscription With Apple Pay", {
        distinct_id: accountData.uid,
        subscription_plan: subscriptionPlan.name,
        is_trial: trialSubscription,
        is_referral: referrerUser !== undefined,
        is_desktop: isDesktop,
      });
    }
    await confirmSubscriptionReferral();
    setSubscriptionStage(SubscriptionStage.RECEIPT);
  }, [
    accountData.uid,
    confirmSubscriptionReferral,
    referrerUser,
    setSubscriptionStage,
    subscriptionPlan.name,
    trialSubscription,
  ]);

  const paymentOrRsvpElement = useMemo(() => {
    if (options !== undefined) {
      return (
        <div className="SubscriptionCheckoutPaymentSheet">
          <Elements
            key={options.clientSecret}
            stripe={getStripePromise(accountData.uid)}
            options={options}
          >
            <SubscriptionCheckoutForm
              label={subscriptionPlan.name + " Subscription"}
              amount={finalPrice}
              processing={purchasing}
              setProcessing={setPurchasing}
              isReadyForPayment={true}
              clientSecret={options.clientSecret}
              onConfirmApplePay={onConfirmApplePay}
              checkoutType={
                trialSubscription
                  ? SubscriptionCheckoutType.TRIAL
                  : SubscriptionCheckoutType.NORMAL
              }
              onPress={async (stripe: Stripe, elements: StripeElements) => {
                if (mixpanel) {
                  mixpanel.track("Webapp: Attempting Getting Subscription", {
                    subscription_plan: subscriptionPlan.name,
                    is_trial: trialSubscription,
                    is_referral: referrerUser !== undefined,
                    is_desktop: isDesktop,
                  });
                }
                setPurchasing(true);
                const { error } = trialSubscription
                  ? await stripe.confirmSetup({
                      // For TRIAL Subscriptions
                      // `Elements` instance that was used to create the Payment Element
                      elements,
                      redirect: "if_required",
                    })
                  : await stripe.confirmPayment({
                      // For PURCHASING Subscriptions
                      // `Elements` instance that was used to create the Payment Element
                      elements,
                      redirect: "if_required",
                    });

                if (error) {
                  // This point will only be reached if there is an immediate error when
                  // confirming the payment. Show error to your customer (for example, payment
                  // details incomplete)
                  console.error(error.message);
                  setAlertText({
                    heading:
                      error.message ??
                      "An error occurred when entering your info.",
                    subHeading: "Please try again.",
                  });
                  mixpanel.track("Webapp: Error Getting Subscription", {
                    subscription_plan: subscriptionPlan.name,
                    is_trial: trialSubscription,
                    is_referral: referrerUser !== undefined,
                    error_message: error.message,
                  });
                  setPurchasing(false);
                } else {
                  if (mixpanel) {
                    mixpanel.track("Webapp: Getting Subscription", {
                      distinct_id: accountData.uid,
                      subscription_plan: subscriptionPlan.name,
                      is_trial: trialSubscription,
                      is_referral: referrerUser !== undefined,
                      is_desktop: isDesktop,
                    });
                  }
                  await confirmSubscriptionReferral();
                  // dispatch(accountActions.updateSubscriptionPhone())
                  setSubscriptionStage(SubscriptionStage.RECEIPT);
                }
              }}
            />
          </Elements>
        </div>
      );
    } else if (showingPaymentSheet) {
      return (
        <div className="ColumnCentering">
          <CircularProgress style={{ color: Colors.GRAY1 }} size={20} />
        </div>
      );
    } else {
      return undefined;
    }
  }, [
    accountData.uid,
    confirmSubscriptionReferral,
    finalPrice,
    onConfirmApplePay,
    options,
    purchasing,
    referrerUser,
    setSubscriptionStage,
    showingPaymentSheet,
    subscriptionPlan.name,
    trialSubscription,
  ]);

  return (
    <>
      <div
        className="ColumnNormal"
        style={{ height: options ? "auto" : "100%" }}
      >
        <div className="ColumnCentering" style={{ gap: 24, paddingBlock: 28 }}>
          <StandardBorderedContainer
            containerStyles={{
              padding: 14,
              display: "flex",
              flexDirection: "row",
              gap: 7,
              cursor: "pointer",
            }}
            onPress={() => setSubscriptionStage(SubscriptionStage.OVERVIEW)}
          >
            <Icon
              icon="mdi:chevron-left"
              height={20}
              style={{ color: Colors.BLACK, marginTop: -1 }}
            />
            <span style={styles.headerText}>
              Continuing with {subscriptionPlan.name}
            </span>
          </StandardBorderedContainer>
          <span style={{ fontSize: 32, fontWeight: 500 }}>Payment</span>
        </div>
        <div
          className="ColumnNormal HideScrollbar"
          style={{
            alignItems: "center",
            backgroundColor: Colors.WHITE1,
            padding: "30px 80px 100px 80px",
            height: "100%",
          }}
        >
          <StandardBorderedContainer
            containerStyles={{
              backgroundColor: Colors.WHITE,
              padding: 14,
              borderRadius: 20,
              width: 362,
              display: "flex",
              flexDirection: "column",
              gap: 24,
            }}
          >
            <div className="ColumnNormal" style={{ gap: 14 }}>
              <div className="ColumnNormal" style={{ gap: 7 }}>
                <span style={styles.headerText}>
                  Enter email{" "}
                  <span style={{ fontSize: 12, color: Colors.GRAY2 }}>
                    (required to receive receipt)
                  </span>
                </span>
                <div className="ColumnNormal" style={{ gap: 4 }}>
                  <CustomTextField
                    value={email}
                    inputMode="email"
                    placeholder="Enter Email"
                    borderRadius={12}
                    backgroundColor={Colors.WHITE}
                    onChange={(change: any) => {
                      setEmailError(false);
                      setEmail(change.target.value);
                    }}
                    error={emailError}
                  />
                  {emailError ? (
                    <span style={styles.errorText}>Email required</span>
                  ) : null}
                </div>
              </div>
              <div className="ColumnNormal" style={{ gap: 7 }}>
                <span style={styles.headerText}>Billing address</span>
                <Elements
                  stripe={getStripePromise(accountData.uid)}
                  options={{ mode: "setup", currency: "usd" }}
                >
                  <SubscriptionBillingAddress
                    defaultName={accountData.fullName}
                    onElementsReady={setAddressElements}
                    onChange={(event) => {
                      setAddressComplete(event.complete);
                      setBillingAddress(
                        event.complete ? event.value.address : undefined
                      );
                    }}
                  />
                </Elements>
              </div>
              <div>
                {!showCodeInput ? (
                  <RectangleButton
                    buttonLabel="Redeem a Promo Code"
                    onPress={() => setShowCodeInput(!showCodeInput)}
                    altColor={Colors.WHITE1}
                    altTextColor={Colors.BLACK}
                    containerStyles={{ border: `1.5px solid ${Colors.GRAY11}` }}
                  />
                ) : (
                  <div className="ColumnNormal" style={{ gap: 4 }}>
                    <div className="AlignedRowSpaced">
                      <CustomTextField
                        value={codeInput}
                        disabled={validPromoCode}
                        inputMode="text"
                        placeholder="Enter Code"
                        borderRadius={12}
                        onChange={(name: any) => {
                          setApplyCodeError("");
                          setCodeInput(name.target.value.toUpperCase());
                        }}
                        error={applyCodeError !== ""}
                        endAdornment={
                          validPromoCode ? (
                            <Icon
                              icon={
                                validPromoCode
                                  ? "ion:checkmark-circle"
                                  : "ion:close-circle"
                              }
                              height={20}
                              style={{
                                marginRight: 10,
                                color: validPromoCode
                                  ? Colors.GREEN2
                                  : Colors.RED3,
                              }}
                            />
                          ) : undefined
                        }
                        width="50%"
                      />
                      <div className="PromoCodeApplyView">
                        {trialSubscription ? (
                          <span style={styles.buttonText}>
                            -{convertToMoney(subscriptionPlan.price / 100)}
                          </span>
                        ) : referrerUser ? (
                          <span style={styles.buttonText}>Applied!</span>
                        ) : (
                          <RectangleButton
                            buttonLabel={
                              <span className="bodyMedium">
                                {"Apply Promo"}
                              </span>
                            }
                            onPress={verifyCodeOnPress}
                            altColor={Colors.BLACK}
                            altTextColor={Colors.WHITE}
                            containerStyles={{
                              padding: 10,
                              whiteSpace: "nowrap",
                            }}
                          />
                        )}
                      </div>
                    </div>
                    <div style={{ width: 260 }}>
                      {applyCodeError ? (
                        <span style={{ ...styles.errorText }}>
                          {applyCodeError}
                        </span>
                      ) : referrerUser ? (
                        <span className="bodySmallRegular">
                          Redeeming {referrerUser.fullName}&apos;s referral code
                        </span>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="ColumnNormal" style={{ gap: 14 }}>
              {/* Tax-inclusive Total with a collapsible breakdown (Subtotal,
                  Discount, Tax), shared with the event checkout summary. */}
              <CheckoutTotalSummary
                totalValue={convertToMoney((finalPrice + taxAmount) / 100)}
                expandable={
                  taxAmount > 0 || finalPrice !== subscriptionPlan.price
                }
                lineItems={[
                  {
                    label: "Subtotal",
                    value: convertToMoney(subscriptionPlan.price / 100),
                  },
                  ...(finalPrice !== subscriptionPlan.price
                    ? [
                        {
                          label: "Discount Applied",
                          value: `-${convertToMoney(
                            (subscriptionPlan.price - finalPrice) / 100
                          )}`,
                        },
                      ]
                    : []),
                  ...(taxAmount > 0
                    ? [{ label: "Tax", value: convertToMoney(taxAmount / 100) }]
                    : []),
                ]}
              />
              <AgreeToTerms
                agreed={agreedToTerms}
                setAgreed={setAgreedToTerms}
                error={agreeToTermsError}
                setError={setAgreeToTermsError}
                disabled={showingPaymentSheet}
                nonRefundable
              />
            </div>
            <div>
              {paymentOrRsvpElement}
              {!showingPaymentSheet ? (
                <RectangleButton
                  buttonLabel="Continue to Payment"
                  onPress={subscribe}
                  altPaddingVert={14}
                  altPaddingHorz={14}
                  altColor={Colors.BLACK}
                  altTextColor={Colors.WHITE}
                />
              ) : null}
            </div>
          </StandardBorderedContainer>
        </div>
      </div>
      <AlertContainer
        headerComp={alertText.heading}
        subHeaderComp={
          alertText.subHeading !== "" ? alertText.subHeading : undefined
        }
        closeModal={() => {
          // window.location.reload();
          setAlertText({ heading: "", subHeading: "" });
        }}
        hideModal={alertText.heading === "" && alertText.subHeading === ""}
      />
    </>
  );
});

export default SubscriptionCheckout;
